import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../stores/useAuthStore';
import { useUIStore } from '../../stores/useUIStore';
import { useChatStore } from '../../stores/useChatStore';
import { useInteractionStore } from '../../stores/useInteractionStore';
import { getProfileLink } from '../../utils/getProfileLink';
import { getMediaUrl } from '../../utils/imageUtils';

const SmartNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [context, setContext] = useState('entity');
  const [isMounted, setIsMounted] = useState(false);

  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const currentUser = useAuthStore((state) => state.currentUser);
  const openModal = useUIStore((state) => state.openModal);
  const totalUnreadMessages = useChatStore((state) => state.totalUnreadCount);
  const totalUnreadNotifications = useInteractionStore((state) => state.unreadCount);

  const location = useLocation();
  const navigate = useNavigate();

  // Ocultar SmartNavigation si estamos en /admin
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLandingPage = location.pathname === '/';
  const sessionActive = Boolean(isLoggedIn && currentUser?._id);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const hasActiveTools = currentUser?.creatorToolsActive === undefined
    ? (currentUser?.role === 'blogger' || currentUser?.roles?.includes('blogger'))
    : currentUser?.creatorToolsActive;

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/stores') || path.startsWith('/jobs') || path.startsWith('/ads') || path.startsWith('/my-ads')) {
      setContext('business');
    } else {
      setContext('entity');
    }
  }, [location.pathname]);

  const getMenuSections = () => {
    let profilePath = '/auth';
    if (currentUser) {
      profilePath = hasActiveTools ? `/blogger/${currentUser.username}` : getProfileLink(currentUser);
    }

    const pillarItems = [
      { name: 'Noticias', path: '/news', icon: 'fas fa-rss', color: 'text-orange-500' },
      { name: 'Feed Social', path: '/feed', icon: 'fas fa-newspaper', color: 'text-purple-500', protected: true },
      { name: 'Mensajes', path: '/messenger', icon: 'fas fa-envelope', color: 'text-cyan-500', protected: true, badge: totalUnreadMessages },
      { name: 'Notificaciones', path: '/notifications', icon: 'fas fa-bell', color: 'text-red-500', protected: true, badge: totalUnreadNotifications },
      { name: 'Explorar', path: '/explore', icon: 'fas fa-users', color: 'text-teal-500', protected: true },
      { name: 'Bloggers', path: '/bloggers', icon: 'fas fa-feather-alt', color: 'text-pink-500' },
      { name: 'Ágora Central', path: '/agora-central', icon: 'fas fa-crown', color: 'text-[#D4AF37]' },
      { name: 'Podcasts', path: '/podcasts', icon: 'fas fa-microphone-alt', color: 'text-[#D4AF37]' },
    ];

    const businessItems = [
      { name: 'Empleos', path: '/jobs', icon: 'fas fa-briefcase', color: 'text-green-500' },
      { name: 'Tiendas', path: '/stores', icon: 'fas fa-store', color: 'text-yellow-500' },
    ];

    const accountItems = [
      sessionActive
        ? {
            name: 'Mi Perfil',
            path: profilePath,
            icon: 'fas fa-user-circle',
            color: 'text-blue-500',
            protected: true,
            avatarUrl: currentUser?.avatar || '',
          }
        : {
            name: 'Iniciar Sesión',
            path: '/auth',
            icon: 'fas fa-sign-in-alt',
            color: 'text-emerald-500',
            protected: false,
          },
    ];

    return [
      { title: 'Pilares', items: pillarItems },
      { title: 'Negocios', items: businessItems },
      { title: 'Cuenta', items: accountItems },
    ];
  };

  const handleProtectedAction = (event, item) => {
    if (item.protected && !isLoggedIn) {
      event.preventDefault();
      openModal('loginModal');
    }
  };

  const menuSections = getMenuSections();
  const isEntityContext = context === 'entity';

  const sidebarVariants = {
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    closed: { x: '-100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
  };

  const MenuContent = ({ mobile = false }) => (
    <div className="py-4 space-y-4">
      {menuSections.map((section) => (
        <div key={section.title} className="px-1">
          {(isExpanded || mobile) && (
            <p className={`px-3 pt-1 pb-2 text-[11px] uppercase tracking-wider ${(isEntityContext || isLandingPage) ? 'text-white/50' : 'text-gray-400'}`}>
              {section.title}
            </p>
          )}
          <ul className="space-y-2">
            {section.items.map((item) => {
              const isProtectedAndLoggedOut = item.protected && !isLoggedIn;
              return (
                <li key={`${section.title}-${item.name}`}>
                  <NavLink
                    to={item.path}
                    onClick={(event) => {
                      handleProtectedAction(event, item);
                      if (mobile) setIsMobileMenuOpen(false);
                    }}
                    className={({ isActive }) => {
                      const baseClasses = 'relative flex items-center categoria-p px-2 py-2 mx-2 rounded-xl transition-all duration-300 group';
                      const activeClasses = (isEntityContext || isLandingPage) ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600';
                      const inactiveClasses = (isEntityContext || isLandingPage) ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-blue-600';
                      const protectionClass = isProtectedAndLoggedOut ? 'opacity-70' : '';

                      return `${baseClasses} ${protectionClass} ${isActive && !isProtectedAndLoggedOut ? activeClasses : inactiveClasses}`;
                    }}
                  >
                    <div className={`relative flex items-center justify-center w-6 h-6 transition-transform duration-300 group-hover:scale-110 ${(isExpanded || mobile) ? 'mr-3' : 'mx-auto'}`}>
                      {item.avatarUrl ? (
                        <img
                          src={getMediaUrl(item.avatarUrl)}
                          alt="Perfil"
                          className="w-6 h-6 rounded-full object-cover border border-white/40"
                        />
                      ) : (
                        <i className={`${item.icon} text-lg ${item.color} drop-shadow-sm`}></i>
                      )}
                      {item.badge > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] border border-white">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </div>
                    <span className={`whitespace-nowrap font-medium text-sm transition-all duration-300 ${(isExpanded || mobile) ? 'opacity-100' : 'opacity-0 hidden'}`}>
                      {item.name}
                    </span>
                    {isProtectedAndLoggedOut && (isExpanded || mobile) && (
                      <i className="fas fa-lock text-[10px] ml-auto text-gray-400"></i>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );

  if (isAdminRoute) return null;

  return (
    <>
      <div
        className={`fixed top-32 mt-6 left-4 z-[9999] hidden lg:block transition-all duration-700 ${isMounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'} translate-y-0 opacity-100`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <nav className={`rounded-2xl border transition-all duration-500 ease-in-out overflow-hidden shadow-xl ${(isLandingPage || isEntityContext) ? 'bg-black/80 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'} ${isExpanded ? 'w-60' : 'w-16'}`}>
          <MenuContent />
        </nav>
      </div>

      <AnimatePresence>
        {!isMobileMenuOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsMobileMenuOpen(true)}
            className="fixed bottom-5 left-5 z-[9998] flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg lg:hidden"
          >
            <i className="fas fa-bars text-xl"></i>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm lg:hidden"
            />
            <motion.nav
              initial="closed"
              animate="open"
              exit="closed"
              variants={sidebarVariants}
              className="fixed top-0 left-0 z-[9999] h-full w-[280px] bg-black/90 text-white backdrop-blur-xl lg:hidden border-r border-white/10"
            >
              <div className="flex h-full flex-col p-6">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-xl font-bold text-orange-500 italic">ÁGORA WEB</span>
                  <button onClick={() => setIsMobileMenuOpen(false)} className="text-white/70 hover:text-white">
                    <i className="fas fa-times text-2xl"></i>
                  </button>
                </div>

                <MenuContent mobile />

                <div className="mt-auto border-t border-white/10 pt-6">
                  {isLoggedIn && currentUser ? (
                    <div className="flex items-center space-x-3">
                      <img src={getMediaUrl(currentUser.avatar)} className="h-10 w-10 rounded-full border border-orange-500 object-cover" alt="User" />
                      <div className="overflow-hidden">
                        <p className="font-bold truncate text-sm">{currentUser.username}</p>
                        <p className="text-xs text-gray-400 truncate">{currentUser.email}</p>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate('/auth');
                      }}
                      className="w-full py-3 bg-[#D4AF37] text-black rounded-xl font-bold flex items-center justify-center space-x-2 hover:bg-[#B5952F] transition-colors"
                    >
                      <i className="fas fa-sign-in-alt"></i>
                      <span>Iniciar Sesión</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default SmartNavigation;
